import { DateTime } from 'luxon';

const OSLO_TZ = 'Europe/Oslo';

/**
 * @typedef {(Date | string | number | null | undefined)} DateInput
 */

/**
 * Normalize various date-like inputs into a valid Date instance.
 * @param {DateInput} value
 * @returns {Date | null}
 */
export function parseDateInput(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value !== 'string') {
    return null;
  }

  let dt = DateTime.fromISO(value, { zone: OSLO_TZ });
  if (!dt.isValid) dt = DateTime.fromFormat(value, 'dd-LL-yyyy', { zone: OSLO_TZ });
  if (!dt.isValid) dt = DateTime.fromFormat(value, 'dd.LL.yyyy', { zone: OSLO_TZ });
  if (!dt.isValid) dt = DateTime.fromFormat(value, 'yyyy-LL-dd', { zone: OSLO_TZ });
  if (!dt.isValid) {
    const timestamp = Date.parse(value);
    if (!Number.isFinite(timestamp)) return null;
    const fallback = new Date(timestamp);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  const iso = dt.toUTC().toISO();
  if (!iso) return null;
  const normalized = new Date(iso);
  return Number.isNaN(normalized.getTime()) ? null : normalized;
}

/**
 * Format a date-like value into DD-MM-YYYY using the Oslo timezone.
 * @param {DateInput} value
 * @returns {string}
 */
export function formatDDMMYYYY(value) {
  const parsed = parseDateInput(value);
  if (!parsed) return '';
  return DateTime.fromJSDate(parsed, { zone: OSLO_TZ }).toFormat('dd.LL.yyyy');
}

/**
 * Format a date-like value into HH:mm using the Oslo timezone.
 * @param {DateInput} value
 * @returns {string}
 */
export function formatHHmm(value) {
  const parsed = parseDateInput(value);
  if (!parsed) return '';
  return DateTime.fromJSDate(parsed, { zone: OSLO_TZ }).toFormat('HH:mm');
}

/**
 * Convert an input value into an ISO string normalized to UTC.
 * @param {DateInput} value
 * @returns {string | null}
 */
export function toIsoUtcString(value) {
  const parsed = parseDateInput(value);
  if (!parsed) return null;
  return DateTime.fromJSDate(parsed, { zone: OSLO_TZ }).toUTC().toISO();
}

export { OSLO_TZ };
